"use client"

import { useState, useEffect, useRef, useActionState, useTransition } from "react"
import { useTranslations } from "next-intl"
import { AlertCircleIcon, CheckCircle2Icon, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { InlineNotification } from "@/components/ui/InlineNotification"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PhoneInput } from "@/components/ui/phone-input"
import { Textarea } from "@/components/ui/textarea"
import {
  submitContactAction,
  type ContactActionState,
} from "@/app/[locale]/(storefront)/contact/actions"
import { contactFormSchema } from "@/lib/schemas/contact"

type ContactFormState = ContactActionState

const initialState: ContactFormState = {
  success: false,
  error: null,
  fieldErrors: null,
  submittedAt: 0,
}

// E3: known top-level error keys returned by submitContactAction. Anything outside
// this set falls back to "contactSubmitError" so we never feed a raw API string to
// next-intl (which would surface MISSING_MESSAGE in the UI).
const ALLOWED_CONTACT_ERROR_KEYS = ["contactSubmitError", "rateLimitExceeded"] as const
type AllowedContactErrorKey = (typeof ALLOWED_CONTACT_ERROR_KEYS)[number]

function isAllowedContactErrorKey(value: string): value is AllowedContactErrorKey {
  return (ALLOWED_CONTACT_ERROR_KEYS as readonly string[]).includes(value)
}

// E3: known field-level Zod error keys defined in src/lib/schemas/contact.ts.
const ALLOWED_CONTACT_FIELD_ERROR_KEYS = [
  "requiredField",
  "nameTooLong",
  "invalidEmail",
  "invalidPhone",
  "messageTooShort",
  "messageTooLong",
] as const
type AllowedContactFieldErrorKey = (typeof ALLOWED_CONTACT_FIELD_ERROR_KEYS)[number]

function isAllowedContactFieldErrorKey(
  value: string | undefined,
): value is AllowedContactFieldErrorKey {
  return (
    typeof value === "string" &&
    (ALLOWED_CONTACT_FIELD_ERROR_KEYS as readonly string[]).includes(value)
  )
}

export function ContactForm() {
  const t = useTranslations("contact")
  const [isPending, startTransition] = useTransition()
  const [formState, formAction] = useActionState(submitContactAction, initialState)

  const [topic, setTopic] = useState("general")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  // PhoneInput is uncontrolled (carries its value in a hidden input). Bump this key to
  // remount and reset it after a successful submit.
  const [phoneResetKey, setPhoneResetKey] = useState(0)
  // Client-side validation errors — gate the Server Action so invalid submits
  // don't burn rate-limit budget. Schema messages are already i18n keys.
  const [clientErrors, setClientErrors] = useState<Record<string, string[]> | null>(null)

  // Client errors take precedence over the last server response
  const fieldErrors = clientErrors ?? formState.fieldErrors

  // Refs for focus management on validation error (AC 4, AC 8 / FM7)
  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const messageRef = useRef<HTMLTextAreaElement>(null)

  // Success: clear all fields. The confirmation itself renders as an inline
  // notification in the form body — the storefront convention for submission
  // confirmations that should persist until the user acts or navigates away,
  // rather than a floating toast. Depend on submittedAt so consecutive identical
  // success states re-trigger the field clear.
  useEffect(() => {
    if (formState.submittedAt > 0 && formState.success) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear controlled fields once the server confirms a successful submission
      setName("")
      setEmail("")
      setMessage("")
      setTopic("general")
      setPhoneResetKey((k) => k + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.submittedAt])

  // Top-level submission outcome, rendered inline (see the form body). Success is a
  // polite status banner; a server error (e.g. rate limit) is an assertive alert.
  // Field-level validation errors render per-field, so the error banner is
  // suppressed while client-side validation errors are showing.
  const showSuccess = formState.success
  const errorKey =
    !clientErrors && !formState.success && formState.error
      ? isAllowedContactErrorKey(formState.error)
        ? formState.error
        : "contactSubmitError"
      : null

  // Focus first field with error on validation failure (AC 4, AC 8 / FM7)
  useEffect(() => {
    if (!formState.success && fieldErrors) {
      if (fieldErrors.name) {
        nameRef.current?.focus()
      } else if (fieldErrors.email) {
        emailRef.current?.focus()
      } else if (fieldErrors.message) {
        messageRef.current?.focus()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrors])

  // E2: onSubmit + e.preventDefault() + startTransition(() => formAction(fd))
  // NEVER use action={formAction} — React 19 auto-resets all fields on completion
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData()
    const form = e.currentTarget
    // PhoneInput keeps its E.164 value in a hidden input rather than React state — read it at
    // submit time so the latest value is captured even without a blur event.
    const phoneEl = form.querySelector<HTMLInputElement>('input[name="phone"]')
    const phone = phoneEl?.value ?? ""
    fd.set("topic", topic)
    fd.set("name", name)
    fd.set("email", email)
    fd.set("phone", phone)
    fd.set("message", message)
    // Include honeypot field value (if any) from the form
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website"]')
    fd.set("website", honeypotEl?.value ?? "")

    // Client-side gate — schema returns translation-key error messages
    const parsed = contactFormSchema.safeParse({ topic, name, email, phone, message })
    if (!parsed.success) {
      const errs: Record<string, string[]> = {}
      for (const [k, v] of Object.entries(parsed.error.flatten().fieldErrors)) {
        errs[k] = v ?? []
      }
      setClientErrors(errs)
      return
    }
    setClientErrors(null)
    startTransition(() => {
      formAction(fd)
    })
  }

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
      {/* Submission outcome — inline, not a floating toast (AC 2 / AC 3) */}
      {showSuccess && (
        <div data-testid="contact-success">
          <InlineNotification variant="success" icon={<CheckCircle2Icon />}>
            {t("contactSuccess")}
          </InlineNotification>
        </div>
      )}

      {errorKey && (
        <div data-testid="contact-error">
          <InlineNotification variant="error" icon={<AlertCircleIcon />}>
            {t(errorKey)}
          </InlineNotification>
        </div>
      )}

      {/* Honeypot field — hidden from real users, filled by bots (AC 9) */}
      <input
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      {/* Aria-live region for screen reader announcements (AC 8) — rendered always, content conditional */}
      <div role="alert" aria-live="polite" className="sr-only">
        {fieldErrors && t("validationErrors")}
      </div>

      {/* Topic / subject — Trello #106. Native select styled like RegisterForm gender. */}
      <div className="space-y-1">
        <Label htmlFor="contact-topic">{t("topicLabel")}</Label>
        <div className="relative">
          <select
            id="contact-topic"
            name="topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={isPending}
            className="h-10 w-full appearance-none rounded-lg border border-input bg-transparent px-3 py-1.5 pr-10 text-base outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 md:text-sm"
          >
            <option value="general">{t("topics.general")}</option>
            <option value="press">{t("topics.press")}</option>
            <option value="collaborations">{t("topics.collaborations")}</option>
            <option value="events">{t("topics.events")}</option>
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Name field */}
      <div className="space-y-1">
        <Label htmlFor="contact-name" required>
          {t("nameLabel")}
        </Label>
        <Input
          ref={nameRef}
          id="contact-name"
          name="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isPending}
          autoComplete="name"
          required
          aria-invalid={fieldErrors?.name ? "true" : undefined}
          aria-describedby={fieldErrors?.name ? "contact-name-error" : undefined}
          placeholder={t("namePlaceholder")}
        />
        {fieldErrors?.name && (
          <p
            id="contact-name-error"
            data-testid="contact-name-error"
            className="text-sm text-destructive"
          >
            {t(
              isAllowedContactFieldErrorKey(fieldErrors.name[0])
                ? fieldErrors.name[0]
                : "requiredField",
            )}
          </p>
        )}
      </div>

      {/* Email field */}
      <div className="space-y-1">
        <Label htmlFor="contact-email" required>
          {t("emailLabel")}
        </Label>
        <Input
          ref={emailRef}
          id="contact-email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isPending}
          autoComplete="email"
          required
          aria-invalid={fieldErrors?.email ? "true" : undefined}
          aria-describedby={fieldErrors?.email ? "contact-email-error" : undefined}
          placeholder={t("emailPlaceholder")}
        />
        {fieldErrors?.email && (
          <p
            id="contact-email-error"
            data-testid="contact-email-error"
            className="text-sm text-destructive"
          >
            {t(
              isAllowedContactFieldErrorKey(fieldErrors.email[0])
                ? fieldErrors.email[0]
                : "invalidEmail",
            )}
          </p>
        )}
      </div>

      {/* Phone field (optional) */}
      <div className="space-y-1">
        <Label htmlFor="contact-phone">
          {t("phoneLabel")}{" "}
          <span className="text-muted-foreground font-normal text-xs">({t("phoneOptional")})</span>
        </Label>
        <PhoneInput
          key={phoneResetKey}
          id="contact-phone"
          name="phone"
          disabled={isPending}
          aria-invalid={!!fieldErrors?.phone}
          aria-describedby={fieldErrors?.phone ? "contact-phone-error" : undefined}
          placeholder={t("phonePlaceholder")}
        />
        {fieldErrors?.phone && (
          <p
            id="contact-phone-error"
            data-testid="contact-phone-error"
            className="text-sm text-destructive"
          >
            {t(
              isAllowedContactFieldErrorKey(fieldErrors.phone[0])
                ? fieldErrors.phone[0]
                : "invalidPhone",
            )}
          </p>
        )}
      </div>

      {/* Message field */}
      <div className="space-y-1">
        <Label htmlFor="contact-message" required>
          {t("messageLabel")}
        </Label>
        <Textarea
          ref={messageRef}
          id="contact-message"
          name="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isPending}
          rows={5}
          required
          aria-invalid={fieldErrors?.message ? "true" : undefined}
          aria-describedby={fieldErrors?.message ? "contact-message-error" : undefined}
          placeholder={t("messagePlaceholder")}
        />
        {/* Character count display (Task 2.13) — trimmed length matches server-side validation */}
        <p className="text-xs text-muted-foreground text-right">{message.trim().length}/5000</p>
        {fieldErrors?.message && (
          <p
            id="contact-message-error"
            data-testid="contact-message-error"
            className="text-sm text-destructive"
          >
            {t(
              isAllowedContactFieldErrorKey(fieldErrors.message[0])
                ? fieldErrors.message[0]
                : "messageTooShort",
            )}
          </p>
        )}
      </div>

      {/* Submit button */}
      <Button type="submit" disabled={isPending}>
        {isPending ? t("submitting") : t("submit")}
      </Button>
    </form>
  )
}
